import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ExpenseGroup, ExpenseStatus } from '../../../__generated__/graphql';
import { Expense, ExpenseGroupStatusInfo } from '../types';
import { UngroupedExpense, getExpenseType } from './expenseTileUtils';
import { ExpenseTileHeader } from './ExpenseTileHeader';
import { ExpenseTileMetadata } from './ExpenseTileMetadata';
import { ExpenseTileActions } from './ExpenseTileActions';
import { ExpenseChildrenList } from './ExpenseChildrenList';
import type { ExpenseGroupWithRelations } from '../ExpensesTable/ExpensesTable';
import './ExpenseGroupTile.css';

type Props = {
  item: ExpenseGroupWithRelations | UngroupedExpense;
  onClick?: (item: ExpenseGroupWithRelations | UngroupedExpense) => void;
  childExpenses?: Expense[];
  onShowDetails?: (item: ExpenseGroupWithRelations | UngroupedExpense) => void;
  onExpenseClick?: (
    expense: Expense,
    showComments?: boolean,
    showItems?: boolean,
  ) => void;
  isExpanded?: boolean;
  onExpandedChange?: (groupId: string, expanded: boolean) => void;
  isMyExpenses?: boolean;
  onAddExpense?: (expenseGroupId: string) => void;
  onSubmitExpenseGroup?: (expenseGroupId: string) => void;
  onDeleteExpenseGroup?: (
    expenseGroupId: string,
    expenseGroupName: string,
    childExpenseCount: number,
  ) => void;
  onDeleteExpense?: (expenseId: string) => void;
  onExpenseGroupStatusChange?: (info: ExpenseGroupStatusInfo) => Promise<void>;
  isDeleting?: boolean;
  isSubmitting?: boolean;
  invalidExpenseIds?: Set<string>;
};

export type { UngroupedExpense };

export const ExpenseGroupTile: React.FC<Props> = ({
  item,
  onClick,
  childExpenses = [],
  onShowDetails,
  onExpenseClick,
  isExpanded: externalIsExpanded,
  onExpandedChange,
  isMyExpenses = false,
  onAddExpense,
  onSubmitExpenseGroup,
  onDeleteExpenseGroup,
  onDeleteExpense,
  onExpenseGroupStatusChange,
  isDeleting = false,
  isSubmitting = false,
  invalidExpenseIds,
}) => {
  const { t } = useTranslation();
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);

  const isExpanded =
    externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;
  const displayEmployeeName = !isMyExpenses;

  const setIsExpanded = (expanded: boolean) => {
    if (onExpandedChange && item.isGroup) {
      onExpandedChange(item.Id, expanded);
    } else {
      setInternalIsExpanded(expanded);
    }
  };

  const handleExpenseClick = (
    expense: Expense,
    showComments = false,
    showItems = false,
  ) => {
    if (item.isGroup && childExpenses.length > 0) {
      setIsExpanded(true);
    }
    onExpenseClick?.(expense, showComments, showItems);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.isGroup && onDeleteExpenseGroup) {
      onDeleteExpenseGroup(item.Id, item.Name, childExpenses.length);
    } else if (!item.isGroup && onDeleteExpense) {
      onDeleteExpense(item.Id);
    }
  };

  const handleTileClick = () => {
    onClick?.(item);
  };

  const handleShowDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.isGroup) {
      setIsExpanded(!isExpanded);
    }
    onShowDetails?.(item);
  };

  const handleAddExpenseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.isGroup) {
      onAddExpense?.(item.Id);
    }
  };

  const handleSubmitExpenseGroupClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.isGroup) {
      onSubmitExpenseGroup?.(item.Id);
    }
  };

  const shouldShowAddExpenseButton = () => {
    return (
      item.isGroup &&
      isMyExpenses &&
      item.flair__Status__c === ExpenseStatus.Draft &&
      isExpanded
    );
  };

  const shouldShowSubmitButton = () => {
    return (
      item.isGroup &&
      isMyExpenses &&
      item.flair__Status__c === ExpenseStatus.Draft
    );
  };

  const showDeleteButton =
    (item.flair__Status__c === ExpenseStatus.Draft && isMyExpenses) ||
    (item.flair__Status__c === ExpenseStatus.Pending && !isMyExpenses);

  const expenseType = getExpenseType(item, childExpenses.length, t);

  return (
    <div className="expense-group-tile-wrapper">
      <Card
        id={item.Id}
        className={`expense-group-tile ${
          isDeleting ? 'expense-tile-deleting' : ''
        }`}>
        <Card.Body className="expense-group-tile-body">
          <ExpenseTileHeader
            item={item}
            amount={item.totalAmount}
            onDeleteClick={handleDeleteClick}
            showDelete={showDeleteButton}
            onClick={handleTileClick}
          />

          <ExpenseTileMetadata
            item={item}
            displayEmployeeName={displayEmployeeName}
            showSubmitButton={shouldShowSubmitButton()}
            isExpanded={isExpanded}
            isDeleting={isDeleting}
            isSubmitting={isSubmitting}
            childExpensesCount={childExpenses.length}
            onSubmitClick={handleSubmitExpenseGroupClick}
          />

          <ExpenseTileActions
            expenseType={expenseType}
            isGroup={item.isGroup}
            isExpanded={isExpanded}
            isMyExpenses={isMyExpenses}
            expenseGroup={item.isGroup ? (item as ExpenseGroup) : undefined}
            onShowDetailsClick={handleShowDetailsClick}
            onExpenseGroupStatusChange={onExpenseGroupStatusChange}
          />

          {item.isGroup && (
            <ExpenseChildrenList
              childExpenses={childExpenses}
              isMyExpenses={isMyExpenses}
              isExpanded={isExpanded}
              showAddButton={shouldShowAddExpenseButton()}
              onExpenseClick={handleExpenseClick}
              onDeleteExpense={onDeleteExpense}
              onAddExpenseClick={handleAddExpenseClick}
              invalidExpenseIds={invalidExpenseIds}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
